/**
 * wasm_decoder.ts
 *
 * H.265 Wasm fallback decoder wrapping h265web.js (H265webjsPlayer API).
 * Activated ONLY when VideoDecoder.isConfigSupported('hvc1...') returns false
 * (e.g. Firefox, Safari on Intel Mac, old Android devices).
 *
 * h265web.js renders YUV frames via WebGL — GPU-accelerated even in Wasm path.
 *
 * Static files required (served from wasmBaseUrl):
 *   h265web.js, h265web_wasm.js, h265web_wasm.wasm
 *
 * Live NALU feed API (verified against ref/h265web.js/static/h265web.js):
 *   player.append265NaluFrame(nalu: Uint8Array, ptsSec: number)
 *   → posts {type:"demuxer_nalu_cb", payload:{nalu, pts, dts, w, h, isKey:true}}
 *   → decoder worker calls wcodec_feed_data(ptr, len, pts_ms, isKey)
 *
 * Decoder initialisation for live mode (verified against h265web.js source):
 *   After build(), post {type:"probe_ok", payload:{w, h}} to _decoder_worker.
 *   This mirrors what _raw265Entry() does internally for raw265 format URLs.
 *   We patch h265web.js with init_live_mode(w, h) to expose this path cleanly.
 *
 * PTS convention: h265web.js expects pts in SECONDS.
 *   ptsUs (microseconds) → ptsSec = ptsUs / 1_000_000
 *
 * Reference: ref/h265web.js/static/h265web.js — H265webjsPlayer class,
 *   append265NaluFrame(), _raw265Entry(), _decoder_worker message protocol.
 */

import { H265DebugLogger, isH265DebugEnabled } from './h265_debug';

export interface WasmDecoderConfig {
  wasmBaseUrl: string;
  width: number;
  height: number;
  fps: number;
}

export interface WasmDecoderStats {
  decodedFrames: number;
  droppedFrames: number;
}

export class WasmDecoder {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private player: any = null;
  private containerId: string;
  private decodedFrames = 0;
  private initialized = false;
  // Gate: drop all frames until the first IDR arrives so the Wasm ffdecoder
  // worker never sees a P-frame before a reference IDR. This prevents
  // ffdecoder_seek_set(-1) from being triggered at startup (which flushes the
  // DPB before an IDR is in it, causing "Could not find ref with POC N" errors).
  private needsKeyframe = true;
  private destroyed = false;
  private debug: H265DebugLogger;
  private workerInstrumented = false;
  /** W1 probe: monotonic timestamp of last IDR fed to worker. */
  private lastIdrFeedTs = 0;
  /** W2-idr log: suppress repetitive IDR feed logs after the first few. */
  private idrFeedLogCount = 0;

  /**
   * W2 probe (shared across all WasmDecoder instances): most-recent IDR feed
   * timestamp on ANY channel. Used by the global console wrapper to attach
   * `sinceLastIdrFeed` to libavcodec POC errors. Channel attribution is
   * approximate (we cannot tell which decoder emitted the worker error), but
   * the timing relative to the *most recent* IDR feed across the page is
   * sufficient to test W2: "POC reset on IDR mishandled".
   */
  private static lastIdrFeedTsAny = 0;
  private static globalConsoleWrapped = false;

  constructor(containerId: string) {
    this.containerId = containerId;
    this.debug = new H265DebugLogger('h265-debug');
    this.debug.start();
    WasmDecoder._installGlobalConsoleWrap();
  }

  /**
   * W2 probe: install a one-shot wrapper around console.error/warn that
   * detects libavcodec POC / reference errors emitted from the h265web.js
   * decoder worker and re-emits them as `[w2-probe]` lines tagged with the
   * elapsed time since the most recent IDR feed.
   *
   * Hypothesis under test (W2): pixel artifacts (green/purple bursts) cluster
   * shortly after IDR feeds → decoder fails to flush its DPB / reset POC
   * tracking when a new IDR arrives → tries to use stale references.
   *
   * If this is true, the histogram of `sinceLastIdrFeed` for POC errors will
   * peak at small values (0-200ms after IDR). If POC errors instead cluster
   * mid-GOP (500-1000ms), W2 is wrong and points at packet loss / corrupted
   * P-frames as the trigger instead.
   */
  private static _installGlobalConsoleWrap(): void {
    if (WasmDecoder.globalConsoleWrapped) return;
    if (typeof window === 'undefined') return;
    WasmDecoder.globalConsoleWrapped = true;
    const POC_RE = /could not find ref|cabac_max_bin|cu_qp_delta|duplicate poc|pps id out of range|sps id out of range|reference picture/i;
    const wrap = (orig: (...a: unknown[]) => void) =>
      function wrapped(this: unknown, ...args: unknown[]) {
        try {
          const text = args.map((a) => (typeof a === 'string' ? a : '')).join(' ');
          if (POC_RE.test(text)) {
            const sinceIdr =
              WasmDecoder.lastIdrFeedTsAny > 0
                ? performance.now() - WasmDecoder.lastIdrFeedTsAny
                : -1;
            const snippet = text.replace(/\s+/g, ' ').slice(0, 160);
            // eslint-disable-next-line no-console
            console.info(
              `[w2-probe] poc-error sinceLastIdrFeed=${sinceIdr.toFixed(0)}ms text="${snippet}"`,
            );
          }
        } catch { /* never break console */ }
        return orig.apply(this, args as []);
      };
    // eslint-disable-next-line no-console
    console.error = wrap(console.error.bind(console)) as typeof console.error;
    // eslint-disable-next-line no-console
    console.warn = wrap(console.warn.bind(console)) as typeof console.warn;
  }

  /**
   * W3 probe: parse Annex B access units fed to the worker and emit a
   * NALU-type histogram + parameter-set fingerprint. Read-only, never
   * modifies the bytes sent to the decoder.
   *
   * Goal:
   *   - Confirm each IDR carries VPS+SPS+PPS (relay/streamer integrity).
   *   - Detect mid-stream parameter-set drift (encoder reconfig) by
   *     hashing VPS/SPS/PPS bytes and reporting when they change.
   *   - Capture the NALU pattern of P-frames (single TRAIL_R vs split).
   *
   * NAL header layout for HEVC (Annex B):
   *   nal_unit_type = (byte0 >> 1) & 0x3F
   *   Types: 32=VPS, 33=SPS, 34=PPS, 19=IDR_W_RADL, 20=IDR_N_LP, 21=CRA,
   *          1=TRAIL_N, 0=TRAIL_R, 39=PREFIX_SEI, 40=SUFFIX_SEI.
   */
  private static w3FrameSeq = 0;
  private static w3VpsHash = '';
  private static w3SpsHash = '';
  private static w3PpsHash = '';
  private static w3IdrSeq = 0;
  private static _w3HashBytes(b: Uint8Array): string {
    // Tiny FNV-1a 32 — sufficient to detect any parameter-set change.
    let h = 0x811c9dc5;
    for (let i = 0; i < b.length; i++) {
      h ^= b[i];
      h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, '0');
  }
  private static _w3SplitNalus(buf: Uint8Array): Array<{ type: number; len: number; offset: number }> {
    // Locate Annex B start codes (00 00 00 01 or 00 00 01) and slice NALUs.
    const out: Array<{ type: number; len: number; offset: number }> = [];
    const n = buf.length;
    let i = 0;
    const startOffsets: number[] = [];
    while (i + 3 < n) {
      if (buf[i] === 0 && buf[i + 1] === 0) {
        if (buf[i + 2] === 1) { startOffsets.push(i + 3); i += 3; continue; }
        if (buf[i + 2] === 0 && buf[i + 3] === 1) { startOffsets.push(i + 4); i += 4; continue; }
      }
      i++;
    }
    for (let k = 0; k < startOffsets.length; k++) {
      const off = startOffsets[k];
      const end = k + 1 < startOffsets.length ? startOffsets[k + 1] - 3 : n;
      // strip trailing 00s before next start code (3 or 4 byte)
      const nalEnd = end > 0 && buf[end - 1] === 0 ? (end >= 2 && buf[end - 2] === 0 ? end - 1 : end) : end;
      if (off >= nalEnd) continue;
      const nalType = (buf[off] >> 1) & 0x3F;
      out.push({ type: nalType, len: nalEnd - off, offset: off });
    }
    return out;
  }
  private static _w3ProbeFrame(buf: Uint8Array, isKey: boolean): void {
    const seq = ++WasmDecoder.w3FrameSeq;
    const verbose = seq <= 3 || isKey;
    if (!verbose) return;
    let nalus: Array<{ type: number; len: number; offset: number }>;
    try {
      nalus = WasmDecoder._w3SplitNalus(buf);
    } catch {
      return;
    }
    const histo: Record<number, number> = {};
    for (const n of nalus) histo[n.type] = (histo[n.type] || 0) + 1;
    const histoStr = Object.entries(histo)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([t, c]) => `${t}x${c}`)
      .join(',');
    // Order matters for HEVC: VPS,SPS,PPS must precede IDR slices.
    const orderStr = nalus.map((n) => n.type).join('>');
    if (isKey) {
      const idrSeq = ++WasmDecoder.w3IdrSeq;
      const findOne = (t: number) => nalus.find((n) => n.type === t);
      const vps = findOne(32);
      const sps = findOne(33);
      const pps = findOne(34);
      const hash = (n?: { offset: number; len: number }) =>
        n ? WasmDecoder._w3HashBytes(buf.subarray(n.offset, n.offset + n.len)) : 'none';
      const vpsH = hash(vps);
      const spsH = hash(sps);
      const ppsH = hash(pps);
      const vpsChanged = WasmDecoder.w3VpsHash && WasmDecoder.w3VpsHash !== vpsH;
      const spsChanged = WasmDecoder.w3SpsHash && WasmDecoder.w3SpsHash !== spsH;
      const ppsChanged = WasmDecoder.w3PpsHash && WasmDecoder.w3PpsHash !== ppsH;
      WasmDecoder.w3VpsHash = vpsH;
      WasmDecoder.w3SpsHash = spsH;
      WasmDecoder.w3PpsHash = ppsH;
      const flag = (c: boolean, name: string) => (c ? ` ${name}-CHANGED` : '');
      if (idrSeq <= 5 || vpsChanged || spsChanged || ppsChanged) {
        // eslint-disable-next-line no-console
        console.info(
          `[w3-probe] idr#${idrSeq} seq=${seq} bytes=${buf.byteLength} nalus=${nalus.length} order=${orderStr} histo=${histoStr} vps=${vpsH} sps=${spsH} pps=${ppsH}${flag(!!vpsChanged, 'VPS')}${flag(!!spsChanged, 'SPS')}${flag(!!ppsChanged, 'PPS')}${vps && sps && pps ? '' : ' MISSING-PS'}`,
        );
      }
    } else {
      // eslint-disable-next-line no-console
      console.info(
        `[w3-probe] p#${seq} bytes=${buf.byteLength} nalus=${nalus.length} order=${orderStr} histo=${histoStr}`,
      );
    }
  }

  /**
   * Dynamically inject a <script> tag and wait for it to load.
   * No-op if a script with the same src is already in the document.
   */
  private _loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Only skip loading if an EXISTING tag is present AND the script has
      // already run (H265webjsPlayer defined). If a previous load attempt
      // left a tag but the script threw (global not set), we must reload.
      const existing = document.querySelector(`script[src="${src}"]`);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (existing && (window as any).H265webjsPlayer) {
        resolve();
        return;
      }
      // Remove stale tag from a failed prior attempt so we can reload cleanly.
      existing?.remove();
      const s = document.createElement('script');
      s.src = src;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error(`[WasmDecoder] Failed to load script: ${src}`));
      document.head.appendChild(s);
    });
  }

  /**
   * Load and initialise the h265web.js Wasm player.
   * Dynamically loads h265web.js from wasmBaseUrl if not already present in window.
   */
  async build(config: WasmDecoderConfig): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!(window as any).H265webjsPlayer) {
      await this._loadScript(`${config.wasmBaseUrl}h265web.js`);
    }
    if (this.destroyed) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const H265webjsPlayer = (window as any).H265webjsPlayer;
    if (!H265webjsPlayer) {
      throw new Error(
        '[WasmDecoder] h265web.js did not export H265webjsPlayer. ' +
          `Check that ${config.wasmBaseUrl}h265web.js is the correct file.`,
      );
    }

    // Force WASM ffdecoder path (not WebCodec).
    // With WebCodec, h265web.js transfers the canvas to a worker OffscreenCanvas,
    // making the main-thread canvas blank so our captureStream relay reads nothing.
    // Setting webcodec_hevc=false makes _check_is_webcodec() return false, so
    // h265web.js falls through to _check_is_wasm() → CORE_TYPE_WASM_HEVC →
    // _init_ffdecoder_worker() which renders on main thread via cylopengl.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ylSupport = (window as any).YLPlayerSupport;
    if (ylSupport) {
      ylSupport.webcodec_hevc = false;
    }

    this.player = H265webjsPlayer();
    this.player.on_ready_show_done_callback = () => {
      this.decodedFrames++;
    };

    this.player.build({
      player_id: this.containerId,
      base_url: config.wasmBaseUrl,
      wasm_js_uri: 'h265web_wasm.js',
      wasm_wasm_uri: 'h265web_wasm.wasm',
      width: config.width,
      height: config.height,
      fps: config.fps ?? 25,
      auto_play: true,
      ignore_audio: true,
    });
  }

  /**
   * Initialise the Wasm decoder for live NALU push mode.
   *
   * Returns a Promise that resolves once probe_ok has been posted to the
   * ffdecoder worker (i.e. the decoder is truly ready to accept NALUs).
   * Callers MUST await this before flushing any buffered frame queue so that
   * frames are not fed before the Wasm codec context exists.
   *
   * h265web.js is patched to call the onReady callback after probe_ok.
   * Falls back to an immediate resolve if the patch is absent.
   *
   * @param width   Video width (pixels)
   * @param height  Video height (pixels)
   */
  initLiveDecoder(width: number, height: number): Promise<void> {
    if (this.destroyed || !this.player || this.initialized) return Promise.resolve();

    if (typeof this.player.init_live_mode === 'function') {
      return new Promise<void>((resolve) => {
        this.player.init_live_mode(width, height, () => {
          this.initialized = true;
          resolve();
        });
      });
    } else if (this.player._decoder_worker) {
      // Fallback: direct access — mirrors _raw265Entry() probe_ok dispatch
      this.player._decoder_worker.postMessage({
        type: 'probe_ok',
        payload: { w: width, h: height },
      });
      if (this.player._media_info) {
        this.player._media_info.w = width;
        this.player._media_info.h = height;
      }
      this.initialized = true;
      return Promise.resolve();
    } else {
      console.warn('[WasmDecoder] Cannot initialize decoder: no init_live_mode and no _decoder_worker.');
      return Promise.resolve();
    }
  }

  /**
   * Feed a raw NALU into the Wasm decoder (Annex B format).
   *
   * Uses H265webjsPlayer.append265NaluFrame(nalu, ptsSec):
   *   → posts demuxer_nalu_cb to _decoder_worker
   *   → Wasm wcodec_feed_data(ptr, len, pts_ms, isKey)
   *
   * The relay delivers entire encoded frames as one Annex B buffer (access unit):
   *   - IDR keyframe: VPS+SPS+PPS+IDR concatenated in one buffer, isKeyframe=true
   *   - P-frame:      TRAIL_R alone, isKeyframe=false
   *
   * h265web.js's ffdecoder worker uses `ffdecoder_decode_video_frame` which
   * calls `avcodec_send_packet` directly (no parser). The function is designed
   * to receive COMPLETE access units. The worker's `p` (seek-pending) flag
   * logic causes VPS/SPS/PPS sent as individual NALUs to break decoding:
   *   1. ffdecoder_decode_video_frame(VPS alone) → no frame output → returns < 0
   *   2. Worker sets p=true (seek-pending)
   *   3. SPS, PPS are skipped (p=true, not IDR)
   *   4. IDR arrives → ffdecoder_seek_set(-1) flushes decoder state including
   *      parameter sets → IDR decoded without PPS → "PPS id out of range: 0"
   *
   * The h265web.js demuxer worker (normal URL playback) uses libavformat's
   * av_read_frame which produces complete HEVC access units — VPS+SPS+PPS+IDR
   * in ONE packet for keyframes, TRAIL_R alone for P-frames. We must do the same.
   *
   * @param frame       Complete Annex B access unit from the relay
   * @param isKeyframe  true if this is an IDR/CRA frame (relay flag, already correct)
   * @param ptsUs       Presentation timestamp in microseconds
   */
  feedNalu(frame: Uint8Array, isKeyframe: boolean, ptsUs: number): void {
    if (this.destroyed || !this.player || !this.initialized) return;

    // IDR gating: drop all frames until the first IDR access unit arrives.
    if (this.needsKeyframe) {
      if (!isKeyframe) return;
      console.info('[WasmDecoder] first IDR received, len=', frame.byteLength, 'pts=', ptsUs);
      this.debug.event('first IDR received', { bytes: frame.byteLength, ptsUs });
      this.needsKeyframe = false;
    }

    const ptsSec = ptsUs / 1_000_000;
    const worker = this.player._decoder_worker;
    this._instrumentWorker(worker);
    this.debug.noteFeed(isKeyframe, frame.byteLength);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mi = (this.player._media_info as any);

    // Send the complete access unit as one demuxer_nalu_cb message.
    // ffdecoder_decode_video_frame handles multi-NALU Annex B packets
    // (VPS+SPS+PPS+IDR) correctly when received as a single avcodec_send_packet.
    if (worker) {
      worker.postMessage({
        type: 'demuxer_nalu_cb',
        payload: {
          nalu: frame,
          pts: ptsSec,
          dts: ptsSec,
          w: mi?.w ?? 0,
          h: mi?.h ?? 0,
          isKey: isKeyframe,
        },
      });
      if (isKeyframe) {
        const now = performance.now();
        this.lastIdrFeedTs = now;
        WasmDecoder.lastIdrFeedTsAny = now;
        if (++this.idrFeedLogCount <= 2) {
          // eslint-disable-next-line no-console
          console.info(`[w2-idr] feed ts=${now.toFixed(1)}`);
        }
      }
      // W3 probe: NALU breakdown for the first ~30 frames after start AND
      // for every IDR. Lets us tell whether each IDR carries VPS+SPS+PPS
      // (cadence audit) and whether the parameter-set bytes are stable
      // across IDRs (mid-stream encoder reconfig audit).
      //
      // Debug-gated, which it was not. It ran on EVERY IDR in production: a
      // full NAL split of the keyframe, a histogram, three parameter-set
      // hashes, and a console line — all on the main thread, which is also the
      // only hop feeding the audio worklet, and firmware forces a fresh IDR on
      // every viewer keepalive. An audit probe should cost nothing when nobody
      // is auditing. Enable with `localStorage.VGPS_H265_DEBUG = '1'`.
      if (isH265DebugEnabled()) WasmDecoder._w3ProbeFrame(frame, isKeyframe);
    } else {
      // Worker not ready yet — drop frame and reset IDR gate so the next
      // IDR after worker initialisation produces a clean decode start.
      // DO NOT call append265NaluFrame: it always sends isKey:true, which
      // bypasses our keyframe guard and causes "Could not find ref with POC N".
      this.needsKeyframe = true;
    }

    if (isKeyframe) {
      this.decodedFrames++;
    }
  }

  /**
   * Start the cylopengl rendering loop.
   *
   * In URL-based h265web.js usage, play() is called automatically after probe.
   * In live NALU push mode (init_live_mode), play() must be called explicitly
   * to start the setInterval that calls render_yuv420p_texture() on each tick.
   * Without this, decoded YUV frames accumulate in the Wasm cache but are never
   * drawn to the WebGL canvas and video_render_callback never fires.
   */
  startRender(): void {
    if (this.destroyed || !this.player) return;
    if (typeof this.player.play === 'function') {
      this.player.play();
    } else if (this.player._opengl_render?.render_play) {
      // Direct fallback: start the render timer manually
      // render_play(fps, duration, ignore_audio)
      const fps = 25;
      this.player._opengl_render.render_play(fps, 0, true);
    }
  }

  /**
   * Set a callback that fires after every canvas draw (render_yuv420p_texture).
   * Use this to call requestFrame() on the CanvasCaptureMediaStreamTrack so
   * that off-screen canvas content is captured into the MediaStream.
   *
   * The callback is also tapped to bump the debug-logger render counter so
   * `[h265-debug] 1s: ... rendered=N totalRender=N` reflects real WebGL draws
   * (workerInRender is otherwise never incremented since render runs on the
   * main thread, not as a worker message).
   *
   * W1 probe: detect render-loop gaps > GAP_THRESHOLD_MS and log them with
   * the time-since-last-IDR-feed. If gaps cluster around IDR boundaries the
   * GOP-boundary-stutter hypothesis is confirmed.
   */
  setVideoRenderCallback(cb: () => void): void {
    if (this.player) {
      const debug = this.debug;
      const dec = this;
      const GAP_THRESHOLD_MS = 60; // ~2 missed frames at 30 fps
      let lastRenderTs = 0;
      this.player.video_render_callback = () => {
        const now = performance.now();
        if (lastRenderTs > 0) {
          const gap = now - lastRenderTs;
          if (gap > GAP_THRESHOLD_MS) {
            const sinceIdr =
              dec.lastIdrFeedTs > 0 ? now - dec.lastIdrFeedTs : -1;
            // eslint-disable-next-line no-console
            console.warn(
              `[w1-probe] render gap=${gap.toFixed(0)}ms sinceLastIdrFeed=${sinceIdr.toFixed(0)}ms`,
            );
          }
        }
        lastRenderTs = now;
        debug.noteWorkerIn('video_render_callback');
        cb();
      };
    }
  }

  /**
   * Wrap the worker's postMessage / onmessage so the debug logger can count
   * decoder traffic without modifying the minified h265web.js. Idempotent.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _instrumentWorker(worker: any): void {
    if (!worker || this.workerInstrumented) return;
    this.workerInstrumented = true;
    const debug = this.debug;
    const origPost = worker.postMessage.bind(worker);
    worker.postMessage = (msg: unknown, transfer?: Transferable[]) => {
      debug.noteWorkerOut();
      return transfer ? origPost(msg, transfer) : origPost(msg);
    };
    const origOnMessage = worker.onmessage;
    worker.onmessage = (ev: MessageEvent) => {
      try {
        const t = (ev.data && (ev.data as { type?: string }).type) || 'unknown';
        debug.noteWorkerIn(String(t));
      } catch { /* ignore */ }
      if (typeof origOnMessage === 'function') return origOnMessage.call(worker, ev);
    };
    worker.addEventListener('error', (e: ErrorEvent) => {
      console.error('[h265-wasm] worker error', e.message, 'at', e.filename + ':' + e.lineno);
    });
    debug.event('worker instrumented');
  }

  destroy(): void {
    this.destroyed = true;
    this.debug.stop();
    if (this.player) {
      const decoderWorker = this.player._decoder_worker;
      this.player.video_render_callback = null;
      // Stop the renderer before losing its WebGL context in the adapter.
      // h265web's own renderer release method throws in this bundled version.
      this.player._opengl_render?.render_pause?.();
      this.player.release?.();
      // The live ffdecoder worker handles release with self.close() but never
      // acknowledges release_done, so h265web never terminates its Worker
      // handle. Let its release handler run, then reclaim that orphaned heap.
      window.setTimeout(() => decoderWorker?.terminate?.(), 250);
      this.player = null;
    }
    this.initialized = false;
    this.needsKeyframe = true;
  }

  getStats(): WasmDecoderStats {
    return { decodedFrames: this.decodedFrames, droppedFrames: 0 };
  }
}
